/**
 * Loads Google Cast Web Sender (CAF) once. FE-only; receiver is Google’s default media receiver.
 * Availability still requires a Cast device / compatible browser (Chrome, Edge, etc.).
 */

const SCRIPT_URL = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
const SCRIPT_ATTR = 'data-vorakit-cast-sender';

let loadPromise: Promise<boolean> | null = null;

function getCastGlobal(): { cast?: { framework?: unknown }; chrome?: { cast?: unknown } } | undefined {
  if (typeof window === 'undefined') return undefined;
  return window as unknown as { cast?: { framework?: unknown }; chrome?: { cast?: unknown } };
}

/** Resolves true when the Cast framework script loaded and reported APIs available. */
export function loadCastFramework(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  const g = getCastGlobal();
  if (g?.cast?.framework && g?.chrome?.cast) return Promise.resolve(true);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    const done = (ok: boolean) => {
      resolve(ok);
      if (!ok) loadPromise = null;
    };

    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    if (existing) {
      const w = getCastGlobal();
      if (w?.cast?.framework) {
        done(true);
        return;
      }
      existing.addEventListener('load', () => done(Boolean(getCastGlobal()?.cast?.framework)));
      existing.addEventListener('error', () => done(false));
      return;
    }

    const prev = window.__onGCastApiAvailable;
    window.__onGCastApiAvailable = (isAvailable: boolean) => {
      try {
        prev?.(isAvailable);
      } catch {
        /* ignore */
      }
      done(Boolean(isAvailable));
    };

    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.setAttribute(SCRIPT_ATTR, '1');
    s.onerror = () => done(false);
    document.head.appendChild(s);
  });

  return loadPromise;
}

declare global {
  interface Window {
    __onGCastApiAvailable?: (isAvailable: boolean) => void;
  }
}
